import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Input, Stack } from '@chakra-ui/react';
import { PencilSimpleLine, X } from '@phosphor-icons/react';

import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import '@/styles/PhoneInput.css';

import { fetchAPI } from '@/api/fetch';
import { apiUpdateCandidateProfile } from '@/api/services';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';

export default function EditCandidateDialog({
  candidateId,
  firstName,
  lastName,
  email,
  phone,
  linkedin,
  onUpdateCandidate,
  iconOnly = false
}: {
  candidateId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  linkedin: string;
  onUpdateCandidate: (
    first_name: string,
    last_name: string,
    email: string,
    phone: string,
    linkedin: string
  ) => void;
  iconOnly?: boolean;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitError, setSubmitError] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      linkedin
    }
  });

  const handleFormSubmit = async (formData: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    linkedin: string;
  }) => {
    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiUpdateCandidateProfile({
            candidateId: candidateId,
            payload: formData
          })
      });
      if (response.success) {
        onUpdateCandidate(
          formData.first_name,
          formData.last_name,
          formData.email,
          formData.phone,
          formData.linkedin
        );
        setIsDialogOpen(false);
      } else {
        console.error('Failed to update candidate profile', response.message);
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Failed to update candidate profile:', error);
      setSubmitError(true);
    }
  };

  const handleDialogClose = () => {
    reset();
    setSubmitError(false);
    setIsDialogOpen(false);
  };

  return (
    <DialogRoot
      open={isDialogOpen}
      onOpenChange={({ open }) => {
        setIsDialogOpen(open);
        if (open) {
          reset({
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone,
            linkedin: linkedin
          });
        }
      }}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
      unmountOnExit
    >
      <DialogTrigger asChild>
        {iconOnly ? (
          <Box
            bg="neutrals.0"
            border="1px solid"
            borderRadius="8px"
            borderColor="primary.900"
            w="32px"
            h="32px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            _hover={{ cursor: 'pointer', bg: 'neutrals.50' }}
            color="primary.900"
          >
            <PencilSimpleLine size={20} />
          </Box>
        ) : (
          <Button
            bg="background.secondary"
            border="1px solid"
            borderColor="primary.900"
            borderRadius="8px"
            color="primary.900"
            fontSize="14px"
            _hover={{ cursor: 'pointer', bg: 'neutrals.50' }}
          >
            Edit Candidate Info <PencilSimpleLine size={20} color="#4E76FF" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent borderRadius="12px">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Edit Candidate Info</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap="4">
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    First Name
                  </Box>
                }
                invalid={!!errors.first_name}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.first_name?.message}
                  </Box>
                }
              >
                <Input
                  size="lg"
                  placeholder="Enter first name"
                  {...register('first_name', {
                    required: 'First name is required'
                  })}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Last Name
                  </Box>
                }
                invalid={!!errors.last_name}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.last_name?.message}
                  </Box>
                }
              >
                <Input
                  size="lg"
                  placeholder="Enter last name"
                  {...register('last_name', {
                    required: 'Last name is required'
                  })}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500">
                      *{' '}
                    </Box>
                    Email
                  </Box>
                }
                invalid={!!errors.email}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.email?.message}
                  </Box>
                }
              >
                <Input
                  size="lg"
                  placeholder="Enter email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'Invalid email format'
                    }
                  })}
                />
              </Field>
              <Field
                label={<Box fontSize="16px">Phone</Box>}
                invalid={!!errors.phone}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.phone?.message}
                  </Box>
                }
              >
                <Box
                  className={`phone-input-white ${errors.phone ? 'phone-input-error' : ''}`}
                  w="100%"
                >
                  <PhoneInput
                    country={'us'}
                    placeholder="+1 (123) 456-7890"
                    value={watch('phone') || ''}
                    enableSearch
                    disabled={isSubmitting}
                    onChange={(phone) => {
                      setValue('phone', phone, { shouldValidate: true });
                      if (phone && phone.length >= 10) {
                        clearErrors('phone');
                      }
                    }}
                    onBlur={() => {
                      const phoneValue = watch('phone');
                      if (phoneValue && phoneValue.length > 0 && phoneValue.length < 10) {
                        setError('phone', {
                          type: 'manual',
                          message: 'Please enter a valid phone number'
                        });
                      }
                    }}
                    inputProps={{
                      name: 'phone'
                    }}
                  />
                </Box>
              </Field>
              <Field
                label={<Box fontSize="16px">LinkedIn</Box>}
                invalid={!!errors.linkedin}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.linkedin?.message}
                  </Box>
                }
              >
                <Input size="lg" placeholder="Enter linkedin" {...register('linkedin')} />
              </Field>
            </Stack>
            {submitError && (
              <Box color="error.700" fontSize="16px" mt="16px">
                Failed to update candidate info. Please try again.
              </Box>
            )}
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button
                w="120px"
                borderColor="primary.900"
                background="background.secondary"
                color="primary.900"
                borderRadius="8px"
                fontSize="16px"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              type="submit"
              w="120px"
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              loading={isSubmitting}
            >
              Save
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger m="12px" disabled={isSubmitting}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
